import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReferralStatus, UrgencyType } from 'src/app/api-module/control-centre-api-client';
import { referralUrgencyList, statusDisplayMapping } from '../referrals.model';
import { ModalityType } from '@app/shared/model/shared.model';
import { Store } from '@ngxs/store';
import * as ReferralFiltersActions from './referral-filters.actions';
import { ReferralFiltersState } from './referral-filters.state';
import { ModalityForm, ReferralFiltersForm, StatusForm, UrgencyForm } from './referral-filters.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-referral-filters',
    templateUrl: './referral-filters.component.html'
})
export class ReferralFiltersComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    visible: boolean;

    @Output()
    closePopout = new EventEmitter<void>();

    subscriptions = new Subscription();

    statusDisplayMapping = statusDisplayMapping;
    referralStatus = ReferralStatus;
    urgencyType = UrgencyType;

    opening: boolean;
    hasFilters: boolean;

    filtersForm: FormGroup<ReferralFiltersForm>;
    urgencyForm: FormGroup<UrgencyForm>;
    statusForm: FormGroup<StatusForm>;
    modalityForm: FormGroup<ModalityForm>;

    @HostListener('document:keydown.escape', ['$event'])
    @HostListener('document:click', ['$event'])
    clickout(event) {
        // Close filters popout if people click outside it, and wasn't just opened.
        if (this.visible && !this.opening && !this.eRef.nativeElement.contains(event.target)) {
            this.close();
        }
    }

    constructor(private readonly eRef: ElementRef, private readonly store: Store, formBuilder: FormBuilder) {
        this.filtersForm = formBuilder.group<ReferralFiltersForm>({
            urgencies: formBuilder.group<UrgencyForm>({
                Urgent: formBuilder.control<boolean>(false),
                Trauma: formBuilder.control<boolean>(false),
                Stroke: formBuilder.control<boolean>(false)
            }),
            statuses: formBuilder.group<StatusForm>({
                AwaitingAssignment: formBuilder.control<boolean>(false),
                AwaitingDecision: formBuilder.control<boolean>(false),
                AwaitingAcknowledgement: formBuilder.control<boolean>(false),
                Acknowledged: formBuilder.control<boolean>(false),
                OnHold: formBuilder.control<boolean>(false),
                ClinicalDiscussionRequired: formBuilder.control<boolean>(false),
                Refused: formBuilder.control<boolean>(false),
                Cancelled: formBuilder.control<boolean>(false),
                Declined: formBuilder.control<boolean>(false),
                Deferred: formBuilder.control<boolean>(false)
            }),
            modalities: formBuilder.group<ModalityForm>({
                CT: formBuilder.control<boolean>(false),
                MR: formBuilder.control<boolean>(false),
                PlainFilm: formBuilder.control<boolean>(false),
                US: formBuilder.control<boolean>(false),
                NM: formBuilder.control<boolean>(false),
                PT: formBuilder.control<boolean>(false),
                Other: formBuilder.control<boolean>(false)
            })
        });
        this.urgencyForm = this.filtersForm.controls.urgencies;
        this.statusForm = this.filtersForm.controls.statuses;
        this.modalityForm = this.filtersForm.controls.modalities;

        this.subscriptions.add(
            this.filtersForm.valueChanges.subscribe(_formChanges => {
                const modalities: ModalityType[] = this.getFilteredValues(ModalityType, this.modalityForm);
                const urgencies: UrgencyType[] = this.getFilteredValues(UrgencyType, this.urgencyForm);
                const statuses: ReferralStatus[] = this.getFilteredValues(ReferralStatus, this.statusForm);
                if (modalities.length > 0 || urgencies.length > 0 || statuses.length > 0) {
                    this.hasFilters = true;
                }
            })
        );

        this.opening = false;
    }

    ngOnInit(): void {
        this.resetCheckboxes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.filtersForm.pristine) {
            this.resetCheckboxes();
        }

        const visible = changes.visible;
        if (visible) {
            if (visible.previousValue === true && visible.currentValue === false) {
                this.close();
            } else if (!visible.previousValue && visible.currentValue === true) {
                this.opening = true;
                setTimeout(() => {
                    this.opening = false;
                }, 250);
            }
        }
    }

    resetCheckboxes(): void {
        this.hasFilters = false;

        const filteredUrgencies = this.store.selectSnapshot(ReferralFiltersState.getFilteredUrgencies);
        const filteredStatuses = this.store.selectSnapshot(ReferralFiltersState.getFilteredStatuses);
        const filteredModalities = this.store.selectSnapshot(ReferralFiltersState.getFilteredModalities);

        for (const enumValue of referralUrgencyList) {
            const isChecked = filteredUrgencies.includes(enumValue);
            this.urgencyForm.patchValue({ [enumValue]: isChecked });
            this.hasFilters ||= isChecked;
        }

        for (const enumValue of Object.keys(ReferralStatus)) {
            const isChecked = filteredStatuses.includes(ReferralStatus[enumValue]);
            this.statusForm.patchValue({ [enumValue]: isChecked });
            this.hasFilters ||= isChecked;
        }

        for (const enumValue of Object.keys(ModalityType)) {
            const isChecked = filteredModalities.includes(ModalityType[enumValue]);
            this.modalityForm.patchValue({ [enumValue]: isChecked });
            this.hasFilters ||= isChecked;
        }

        this.filtersForm.markAsPristine();
    }

    clearFilters(): void {
        for (const enumValue of referralUrgencyList) {
            this.urgencyForm.patchValue({ [enumValue]: false });
        }

        for (const enumValue of Object.keys(ReferralStatus)) {
            this.statusForm.patchValue({ [enumValue]: false });
        }

        for (const enumValue of Object.keys(ModalityType)) {
            this.modalityForm.patchValue({ [enumValue]: false });
        }

        this.filtersForm.markAsDirty();
    }

    submitFilters(): void {
        const modalities: ModalityType[] = this.getFilteredValues(ModalityType, this.modalityForm);
        const urgencies: UrgencyType[] = this.getFilteredValues(UrgencyType, this.urgencyForm);
        const statuses: ReferralStatus[] = this.getFilteredValues(ReferralStatus, this.statusForm);
        this.store.dispatch(new ReferralFiltersActions.UpdateFilters([...modalities], [...urgencies], [...statuses]));

        this.close();
    }

    close(): void {
        if (this.filtersForm.dirty) {
            this.resetCheckboxes();
        }

        this.closePopout.emit();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private getFilteredValues<TFilter, TFilterOption extends string>(
        enumDefinition: { [key in TFilterOption]: TFilter },
        form: FormGroup
    ): TFilter[] {
        const filteredOptions: TFilter[] = [];

        for (const option in form.value) {
            const checked = form.value[option];

            if (checked) {
                filteredOptions.push(enumDefinition[option as TFilterOption] as TFilter);
            }
        }

        return filteredOptions;
    }
}
